/**
 * LEProjectedPayments - Projected payments table
 *
 * Displays payment breakdown by period (for IO/ARM loans with multiple periods)
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Calculator, Info } from 'lucide-react';
import { LESection } from './LESection';
import { Badge } from '@/components/ui/badge';
import type { ParsedLoanEstimate, ProjectedPaymentPeriod } from './types';

interface LEProjectedPaymentsProps {
  data: ParsedLoanEstimate;
  className?: string;
}

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
}

export const LEProjectedPayments: React.FC<LEProjectedPaymentsProps> = ({
  data,
  className
}) => {
  const payments = data.projectedPayments;
  const hasMultiplePeriods = payments.length > 1;

  return (
    <LESection
      title="Projected Payments"
      icon={<Calculator className="h-4 w-4" />}
      className={className}
    >
      <div className="space-y-4">
        {/* Payment Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-medium text-muted-foreground">
                  Payment
                </th>
                {payments.map((period, idx) => (
                  <th
                    key={idx}
                    className="text-right py-2 font-medium text-muted-foreground min-w-[120px]"
                  >
                    {period.label}
                    {period.isInterestOnly && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        IO
                      </Badge>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {/* Principal & Interest */}
              <tr>
                <td className="py-2">Principal & Interest</td>
                {payments.map((period, idx) => (
                  <td key={idx} className="text-right py-2 tabular-nums font-medium">
                    {formatCurrency(period.principalInterest)}
                    {period.isInterestOnly && (
                      <span className="block text-xs text-muted-foreground">
                        interest only
                      </span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Mortgage Insurance */}
              <tr>
                <td className="py-2">Mortgage Insurance</td>
                {payments.map((period, idx) => (
                  <td
                    key={idx}
                    className={cn(
                      'text-right py-2 tabular-nums',
                      period.mortgageInsurance === 0 && 'text-muted-foreground'
                    )}
                  >
                    {period.mortgageInsurance > 0
                      ? `+ ${formatCurrency(period.mortgageInsurance)}`
                      : '+$0'}
                  </td>
                ))}
              </tr>

              {/* Escrow */}
              <tr>
                <td className="py-2">Estimated Escrow</td>
                {payments.map((period, idx) => (
                  <td
                    key={idx}
                    className={cn(
                      'text-right py-2 tabular-nums',
                      period.escrow === 0 && 'text-muted-foreground'
                    )}
                  >
                    {period.escrow > 0
                      ? `+ ${formatCurrency(period.escrow)}`
                      : '+$0'}
                  </td>
                ))}
              </tr>

              {/* Total */}
              <tr className="border-t-2 font-semibold">
                <td className="py-2">Estimated Total Monthly Payment</td>
                {payments.map((period, idx) => (
                  <td key={idx} className="text-right py-2 tabular-nums text-base">
                    {formatCurrency(period.totalMonthly)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Taxes, Insurance & Assessments */}
        {data.taxesInsuranceAssessments && (
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium">
                  Estimated Taxes, Insurance & Assessments:{' '}
                  <span className="tabular-nums">
                    {formatCurrency(data.taxesInsuranceAssessments.monthlyAmount)}/mo
                  </span>
                </p>
                <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  <p>This estimate includes:</p>
                  <ul className="list-disc list-inside pl-2">
                    {data.taxesInsuranceAssessments.includesPropertyTaxes && (
                      <li>
                        Property Taxes{' '}
                        {data.taxesInsuranceAssessments.inEscrow.propertyTaxes && (
                          <span className="text-green-600">(in escrow)</span>
                        )}
                      </li>
                    )}
                    {data.taxesInsuranceAssessments.includesHomeownersInsurance && (
                      <li>
                        Homeowner's Insurance{' '}
                        {data.taxesInsuranceAssessments.inEscrow.homeownersInsurance && (
                          <span className="text-green-600">(in escrow)</span>
                        )}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Change Notice (for IO/ARM) */}
        {hasMultiplePeriods && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">
                  Your payment will change
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  This loan has an interest-only period. Your payment will increase when
                  the loan begins to amortize.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </LESection>
  );
};

export default LEProjectedPayments;
